import {
  Button,
  ButtonLink,
  Fieldset,
  Header,
  Icon,
  Notification,
  Stack,
  StackItem
} from '@asgard/ui-library';
import chroma from 'chroma-js';
import { PageContentContainer } from 'components/layout/Page.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import { useI18n } from 'hooks/useI18n';

import {
  faFaceSurprise,
  faShareSquare
} from '@fortawesome/pro-light-svg-icons';
import { faUsers } from '@fortawesome/pro-solid-svg-icons';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { YourOfferSalaryResultsStyles } from './YourOfferSalaryResults.styles';
import { YourOfferSalaryResultsProps } from './YourOfferSalaryResults.types';

const ShareModal = dynamic(() => import('components/shared/ShareModal'), {
  ssr: false
});

export const YourOfferSalaryResults: React.FC<YourOfferSalaryResultsProps> = ({
  data,
  experienceRange
}) => {
  const { t } = useI18n('your-offer-salary');
  const { locale, query } = useRouter();

  const RESULT_THRESHOLD = 5;
  const [isShareModalOpened, setIsShareModalOpened] = useState(false);

  const experiences =
    data.stats?.salariesHeatMap.experienceLabels.filter(
      (value) => value !== 'Unspecified'
    ) || [];
  const salaries = data.stats?.salariesHeatMap.salaryLabels || [];
  const heatMap = data.stats?.salariesHeatMap.heatMap || [];
  const min = Math.min(...heatMap.flatMap((row) => row));
  const max = Math.max(...heatMap.flatMap((row) => row));
  const remoteOptions = [
    data.stats?.remoteOptionsPercentages.onSite || 0,
    data.stats?.remoteOptionsPercentages.hybrid || 0,
    data.stats?.remoteOptionsPercentages.remote || 0
  ];

  function getHeatMapTotalPerRow(rowIndex: number): number {
    return heatMap
      .map((row) => row[rowIndex])
      .reduce((acc, value) => acc + value, 0);
  }

  const totalUnspecified = getHeatMapTotalPerRow(0);
  const totalJunior = getHeatMapTotalPerRow(1);
  const totalMid = getHeatMapTotalPerRow(2);
  const totalSenior = getHeatMapTotalPerRow(3);
  const totalPrincipal = getHeatMapTotalPerRow(4);

  const scale = chroma.scale(['#efefef', '#27a39a']).domain([min, max]);

  const expLabels: Record<string, string> = {
    Unspecified: t(`your-offer-salary.result.experiences-undefined`),
    '<2': t(`your-offer-salary.result.experiences-less-2`),
    '2-5': t(`your-offer-salary.result.experiences-2-5`),
    '5-10': t(`your-offer-salary.result.experiences-5-10`),
    '>10': t(`your-offer-salary.result.experiences-more-10`)
  };

  // Get max salary range for each experience level
  const getMaxSalaryRangeForExperience = (): Record<
    string,
    { value: number; salary: string }
  > => {
    const maxSalaryByExperience: Record<
      string,
      { value: number; salary: string }
    > = {};

    experiences.forEach((exp, expIndex) => {
      const colIndex = expIndex + 1; // Skip the 'Unspecified' column (index 0)
      let maxValue = 0;
      let maxSalaryIndex = 0;

      heatMap.forEach((row, rowIndex) => {
        const value = row[colIndex];
        if (value > maxValue) {
          maxValue = value;
          maxSalaryIndex = rowIndex;
        }
      });

      maxSalaryByExperience[exp] = {
        value: maxValue,
        salary: salaries[maxSalaryIndex]
      };
    });

    return maxSalaryByExperience;
  };

  const maxSalaryRangeByExperience = getMaxSalaryRangeForExperience();

  return (
    <YourOfferSalaryResultsStyles.Root>
      <PageContentContainer containerSize="lg">
        {!data.hasEnoughManfredites ? (
          <Stack
            direction="column"
            contentAlign="center"
            className="no-results"
          >
            <Icon icon={faFaceSurprise} size="7xl" />

            <StackItem>
              <Header as="h2" size="4xl" editorial align="center">
                {t('your-offer-salary.result.no-results')}
              </Header>
            </StackItem>
          </Stack>
        ) : (
          <>
            {isShareModalOpened && (
              <ShareModal
                isOpened={isShareModalOpened}
                close={() => setIsShareModalOpened(false)}
                url={`${window.location.origin}/${locale}${getAppUrl(
                  APP_URLS.yourOfferSalary,
                  locale
                )}?${new URLSearchParams(
                  Object.entries(query).reduce((acc, [key, value]) => {
                    if (Array.isArray(value)) {
                      acc[key] = value.join(',');
                    } else if (value !== undefined) {
                      acc[key] = value;
                    }
                    return acc;
                  }, {} as Record<string, string>)
                )}`}
                emailSubject={t(
                  'your-offer-salary.result.share-modal.email-subject'
                )}
                twitterMessage={t('your-offer-salary.result.share-modal.text')}
                telegramMessage={t('your-offer-salary.result.share-modal.text')}
                whatsappMessage={t('your-offer-salary.result.share-modal.text')}
                emailMessage={t(
                  'your-offer-salary.result.share-modal.email-text'
                )}
              />
            )}
            <Stack direction="column" gap="l">
              <YourOfferSalaryResultsStyles.Salaries>
                <StackItem>
                  {data.stats?.manfreditesCount ? (
                    <Header as="h2" size="4xl" align="center">
                      <HtmlTrans
                        i18nKey="your-offer-salary.result.count"
                        ns="your-offer-salary"
                        values={{
                          count:
                            data.stats?.manfreditesCount.toLocaleString(locale)
                        }}
                      />
                    </Header>
                  ) : (
                    <Notification
                      type="warning"
                      message={
                        <HtmlTrans
                          i18nKey="your-offer-salary.result.not-enough"
                          ns="your-offer-salary"
                        />
                      }
                    />
                  )}

                  <Stack direction="row" gap="3xl" contentAlign="center">
                    {totalJunior > RESULT_THRESHOLD &&
                      experienceRange === '<2' && (
                        <div className="number">
                          <strong>{expLabels['<2']}</strong>
                          <p>{maxSalaryRangeByExperience['<2'].salary}</p>
                        </div>
                      )}
                    {totalMid > RESULT_THRESHOLD &&
                      experienceRange === '2-5' && (
                        <div className="number">
                          <strong>{expLabels['2-5']}</strong>
                          <p>{maxSalaryRangeByExperience['2-5'].salary}</p>
                        </div>
                      )}
                    {totalSenior > RESULT_THRESHOLD &&
                      experienceRange === '5-10' && (
                        <div className="number">
                          <strong>{expLabels['5-10']}</strong>
                          <p>{maxSalaryRangeByExperience['5-10'].salary}</p>
                        </div>
                      )}
                    {totalPrincipal > RESULT_THRESHOLD &&
                      experienceRange === '>10' && (
                        <div className="number">
                          <strong>{expLabels['>10']}</strong>
                          <p>{maxSalaryRangeByExperience['>10'].salary}</p>
                        </div>
                      )}
                  </Stack>
                </StackItem>

                <Stack contentAlign="center" alignment="center">
                  <StackItem>
                    <Button
                      schema="brand1"
                      variant="hard"
                      type="button"
                      size="xs"
                      startIcon={faShareSquare}
                      onClick={() => setIsShareModalOpened(true)}
                      label={t('your-offer-salary.result.share')}
                    />
                  </StackItem>

                  <StackItem>
                    <ButtonLink
                      href="#candidatos"
                      schema="brand1"
                      variant="ghost"
                      type="button"
                      size="xs"
                      startIcon={faUsers}
                      label={t('your-offer-salary.result.candidates')}
                    />
                  </StackItem>
                </Stack>

                <Fieldset legend={t('your-offer-salary.result.heatmap-title')}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '7rem' }}></th>
                        {experiences.map((experience) => (
                          <th key={experience}>{expLabels[experience]}</th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {heatMap.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>
                            <strong>{salaries[rowIndex]}</strong>
                          </td>
                          {row.map(
                            (value, colIndex) =>
                              colIndex > 0 && (
                                <td
                                  className={`right ${
                                    Math.max(
                                      ...heatMap.map((row) => row[colIndex])
                                    ) === value && value > 0
                                      ? 'max'
                                      : ''
                                  }`}
                                  key={colIndex}
                                >
                                  <div
                                    style={{
                                      backgroundColor: scale(value).hex()
                                    }}
                                  >
                                    {value.toLocaleString(locale)}%
                                  </div>
                                </td>
                              )
                          )}
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <strong>TOTAL</strong>
                        </td>
                        <td className="right">
                          <div>
                            <strong>
                              {totalJunior.toLocaleString(locale)}%
                            </strong>
                          </div>
                        </td>
                        <td className="right">
                          <div>
                            <strong>{totalMid.toLocaleString(locale)}%</strong>
                          </div>
                        </td>
                        <td className="right">
                          <div>
                            <strong>
                              {totalSenior.toLocaleString(locale)}%
                            </strong>
                          </div>
                        </td>
                        <td className="right">
                          <div>
                            <strong>
                              {totalPrincipal.toLocaleString(locale)}%
                            </strong>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    {totalUnspecified > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={5}>
                            {t('your-offer-salary.result.heatmap-undefined', {
                              value: `${totalUnspecified.toLocaleString(
                                locale,
                                {
                                  minimumFractionDigits: 1
                                }
                              )}%`
                            })}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </Fieldset>
              </YourOfferSalaryResultsStyles.Salaries>
            </Stack>
          </>
        )}
      </PageContentContainer>
    </YourOfferSalaryResultsStyles.Root>
  );
};
